import type { ScriptDescriptor } from '@/types/script.type'
import type {
  InstrumentationProvider,
  InstrumentationSpan,
  InstrumentationUser,
  ObservabilityContext,
} from './Instrumentation'

export class InstrumentationCompose implements InstrumentationProvider {
  private readonly providers: Array<InstrumentationProvider>

  constructor(providers: Array<InstrumentationProvider>) {
    this.providers = providers
  }

  init(options?: Record<string, unknown>): void {
    this.providers.forEach((p) => p.init(options))
  }

  getScripts(): Array<ScriptDescriptor> {
    return this.providers.flatMap((p) => p.getScripts())
  }

  log(
    level: 'info' | 'warn' | 'error' | 'debug',
    message: string,
    context?: ObservabilityContext,
  ): void {
    this.providers.forEach((p) => p.log(level, message, context))
  }

  captureError(error: Error, context?: ObservabilityContext): void {
    this.providers.forEach((p) => p.captureError(error, context))
  }

  startSpan(name: string, context?: ObservabilityContext): InstrumentationSpan {
    const spans = this.providers.map((p) => p.startSpan(name, context))

    return {
      end: (ctx?: ObservabilityContext) => spans.forEach((s) => s.end(ctx)),
      addEvent: (eventName: string, ctx?: ObservabilityContext) =>
        spans.forEach((s) => s.addEvent(eventName, ctx)),
    }
  }

  setUser(id: string, info?: InstrumentationUser): void {
    this.providers.forEach((p) => p.setUser(id, info))
  }

  setMeta(key: string, value: unknown): void {
    this.providers.forEach((p) => p.setMeta(key, value))
  }

  event(name: string, data?: Record<string, unknown>): void {
    this.providers.forEach((p) => p.event(name, data))
  }
}
